/**
 * Life Skills System
 *
 * Skills that level up through world map activities, providing
 * bonuses to gathering, crafting, and equipment enhancement.
 *
 * XP Formula: xpToLevel(n) = 100 * 1.15^(n-1)
 * Level 1→2: 100 XP
 * Level 10→11: 352 XP
 * Level 50→51: 10,836 XP
 */

// ============================================================================
// CONFIGURATION
// ============================================================================

export const LIFE_SKILLS_CONFIG = {
  maxLevel: 50,
  baseXpToLevel: 500,  // 5x slower leveling (was 100)
  xpScaling: 1.15,  // Each level requires 15% more XP

  // XP gained per resource gathered (before bonuses)
  baseXpPerGather: 5,

  // Bonus XP from tile quality (70-130%)
  tileQualityXpBonus: true,
};

// ============================================================================
// XP CALCULATION HELPERS
// ============================================================================

/**
 * Calculate XP required to reach next level
 * @param {number} level - Current level
 * @returns {number} XP needed for next level
 */
export function getXpToNextLevel(level) {
  if (level >= LIFE_SKILLS_CONFIG.maxLevel) return Infinity;
  return Math.floor(
    LIFE_SKILLS_CONFIG.baseXpToLevel *
    Math.pow(LIFE_SKILLS_CONFIG.xpScaling, level - 1)
  );
}

/**
 * Calculate total XP needed to reach a specific level from level 1
 * @param {number} targetLevel - Target level
 * @returns {number} Total XP needed
 */
export function getTotalXpForLevel(targetLevel) {
  let total = 0;
  for (let i = 1; i < targetLevel; i++) {
    total += getXpToNextLevel(i);
  }
  return total;
}

/**
 * Get level from total XP
 * @param {number} totalXp - Total accumulated XP
 * @returns {{ level: number, xpIntoLevel: number, xpForNextLevel: number }}
 */
export function getLevelFromXp(totalXp) {
  let level = 1;
  let remainingXp = totalXp;

  while (level < LIFE_SKILLS_CONFIG.maxLevel) {
    const xpNeeded = getXpToNextLevel(level);
    if (remainingXp < xpNeeded) break;
    remainingXp -= xpNeeded;
    level++;
  }

  return {
    level,
    xpIntoLevel: remainingXp,
    xpForNextLevel: getXpToNextLevel(level),
  };
}

// ============================================================================
// SKILL DEFINITIONS
// ============================================================================

export const LIFE_SKILLS = {
  // === GATHERING SKILLS ===
  lumbering: {
    id: 'lumbering',
    name: 'Lumbering',
    icon: '🪓',
    description: 'Skill in harvesting timber from forests.',
    category: 'gathering',
    // Which resources give XP for this skill
    gatherResources: ['wood'],
    // Which biomes give XP (optional, if empty uses gatherResources)
    biomes: ['forest'],
    // Milestone unlocks at specific levels
    milestones: {
      5:  { yieldBonus: 0.10, description: '+10% wood yield' },
      10: { yieldBonus: 0.05, description: '+5% wood yield (15% total)' },
      15: { yieldBonus: 0.05, rareDropChance: 0.02, description: '+5% yield, 2% rare wood chance' },
      20: { yieldBonus: 0.10, description: '+10% wood yield (30% total)' },
      25: { yieldBonus: 0.05, description: '+5% wood yield (35% total)' },
      30: { yieldBonus: 0.05, autoGatherBonus: 0.25, description: '+5% yield, +25% auto-gather speed' },
      35: { yieldBonus: 0.05, description: '+5% wood yield (45% total)' },
      40: { yieldBonus: 0.10, description: '+10% wood yield (55% total)' },
      45: { yieldBonus: 0.05, rareDropChance: 0.03, description: '+5% yield, +3% rare wood (5% total)' },
      50: { yieldBonus: 0.10, title: 'Master Lumberjack', description: '+10% yield (65% total), Master title' },
    },
  },

  mining: {
    id: 'mining',
    name: 'Mining',
    icon: '⛏️',
    description: 'Expertise in extracting stone and ore from the earth.',
    category: 'gathering',
    gatherResources: ['stone', 'iron', 'coal'],
    biomes: ['mountains', 'volcanic'],
    milestones: {
      5:  { yieldBonus: 0.10, description: '+10% stone/ore yield' },
      10: { yieldBonus: 0.05, description: '+5% yield (15% total)' },
      15: { yieldBonus: 0.05, rareDropChance: 0.02, description: '+5% yield, 2% gem find chance' },
      20: { yieldBonus: 0.10, description: '+10% yield (30% total)' },
      25: { yieldBonus: 0.05, description: '+5% yield (35% total)' },
      30: { yieldBonus: 0.05, autoGatherBonus: 0.25, description: '+5% yield, +25% auto-gather speed' },
      35: { yieldBonus: 0.05, description: '+5% yield (45% total)' },
      40: { yieldBonus: 0.10, description: '+10% yield (55% total)' },
      45: { yieldBonus: 0.05, rareDropChance: 0.03, description: '+5% yield, +3% gem find (5% total)' },
      50: { yieldBonus: 0.10, title: 'Master Miner', description: '+10% yield (65% total), Master title' },
    },
  },

  foraging: {
    id: 'foraging',
    name: 'Foraging',
    icon: '🌿',
    description: 'Knowledge of edible plants and natural remedies.',
    category: 'gathering',
    gatherResources: ['food'],
    biomes: ['forest', 'plains'],
    milestones: {
      5:  { yieldBonus: 0.10, description: '+10% food yield' },
      10: { yieldBonus: 0.05, description: '+5% yield (15% total)' },
      15: { yieldBonus: 0.05, rareDropChance: 0.02, description: '+5% yield, 2% rare herb chance' },
      20: { yieldBonus: 0.10, description: '+10% yield (30% total)' },
      25: { yieldBonus: 0.05, description: '+5% yield (35% total)' },
      30: { yieldBonus: 0.05, autoGatherBonus: 0.25, description: '+5% yield, +25% auto-gather speed' },
      35: { yieldBonus: 0.05, description: '+5% yield (45% total)' },
      40: { yieldBonus: 0.10, description: '+10% yield (55% total)' },
      45: { yieldBonus: 0.05, rareDropChance: 0.03, description: '+5% yield, +3% rare herb (5% total)' },
      50: { yieldBonus: 0.10, title: 'Master Forager', description: '+10% yield (65% total), Master title' },
    },
  },

  prospecting: {
    id: 'prospecting',
    name: 'Prospecting',
    icon: '💰',
    description: 'The art of finding valuable minerals and treasures.',
    category: 'gathering',
    gatherResources: ['gold', 'gems'],
    biomes: ['coastal', 'mountains', 'ruins'],
    milestones: {
      5:  { yieldBonus: 0.10, description: '+10% gold/gem yield' },
      10: { yieldBonus: 0.05, description: '+5% yield (15% total)' },
      15: { yieldBonus: 0.05, rareDropChance: 0.02, description: '+5% yield, 2% treasure find' },
      20: { yieldBonus: 0.10, description: '+10% yield (30% total)' },
      25: { yieldBonus: 0.05, description: '+5% yield (35% total)' },
      30: { yieldBonus: 0.05, autoGatherBonus: 0.25, description: '+5% yield, +25% auto-gather speed' },
      35: { yieldBonus: 0.05, description: '+5% yield (45% total)' },
      40: { yieldBonus: 0.10, description: '+10% yield (55% total)' },
      45: { yieldBonus: 0.05, rareDropChance: 0.03, description: '+5% yield, +3% treasure (5% total)' },
      50: { yieldBonus: 0.10, title: 'Master Prospector', description: '+10% yield (65% total), Master title' },
    },
  },

  archaeology: {
    id: 'archaeology',
    name: 'Archaeology',
    icon: '📜',
    description: 'Uncovering secrets of ancient civilizations.',
    category: 'gathering',
    gatherResources: ['knowledge', 'culture'],
    biomes: ['ruins'],
    milestones: {
      5:  { yieldBonus: 0.10, description: '+10% knowledge yield' },
      10: { yieldBonus: 0.05, description: '+5% yield (15% total)' },
      15: { yieldBonus: 0.05, rareDropChance: 0.02, description: '+5% yield, 2% artifact chance' },
      20: { yieldBonus: 0.10, description: '+10% yield (30% total)' },
      25: { yieldBonus: 0.05, description: '+5% yield (35% total)' },
      30: { yieldBonus: 0.05, autoGatherBonus: 0.25, description: '+5% yield, +25% auto-gather speed' },
      35: { yieldBonus: 0.05, description: '+5% yield (45% total)' },
      40: { yieldBonus: 0.10, description: '+10% yield (55% total)' },
      45: { yieldBonus: 0.05, rareDropChance: 0.03, description: '+5% yield, +3% artifact (5% total)' },
      50: { yieldBonus: 0.10, title: 'Master Archaeologist', description: '+10% yield (65% total), Master title' },
    },
  },

  // === CRAFTING SKILLS ===
  smithing: {
    id: 'smithing',
    name: 'Smithing',
    icon: '🔨',
    description: 'Mastery of metalworking and scroll application.',
    category: 'crafting',
    // XP gained from forging actions
    xpSource: 'forging',
    milestones: {
      5:  { forgeSuccessBonus: 0.02, description: '+2% scroll success rate' },
      10: { forgeSuccessBonus: 0.02, description: '+2% success (4% total)' },
      15: { forgeSuccessBonus: 0.02, description: '+2% success (6% total)' },
      20: { forgeSuccessBonus: 0.02, gemCostReduction: 0.05, description: '+2% success, 5% gem cost reduction' },
      25: { forgeSuccessBonus: 0.02, description: '+2% success (10% total)' },
      30: { forgeSuccessBonus: 0.02, description: '+2% success (12% total)' },
      35: { forgeSuccessBonus: 0.02, gemCostReduction: 0.05, description: '+2% success, +5% gem reduction (10% total)' },
      40: { forgeSuccessBonus: 0.02, description: '+2% success (16% total)' },
      45: { forgeSuccessBonus: 0.02, description: '+2% success (18% total)' },
      50: { forgeSuccessBonus: 0.02, title: 'Master Smith', description: '+2% success (20% total), Master title' },
    },
  },

  enchanting: {
    id: 'enchanting',
    name: 'Enchanting',
    icon: '✨',
    description: 'The magical art of star force enhancement.',
    category: 'crafting',
    // XP gained from star force actions
    xpSource: 'starforce',
    milestones: {
      5:  { enhanceSuccessBonus: 0.01, description: '+1% enhance success rate' },
      10: { enhanceSuccessBonus: 0.01, description: '+1% success (2% total)' },
      15: { enhanceSuccessBonus: 0.01, essenceCostReduction: 0.05, description: '+1% success, 5% essence cost reduction' },
      20: { enhanceSuccessBonus: 0.01, description: '+1% success (4% total)' },
      25: { enhanceSuccessBonus: 0.01, description: '+1% success (5% total)' },
      30: { enhanceSuccessBonus: 0.01, essenceCostReduction: 0.05, description: '+1% success, +5% essence reduction (10% total)' },
      35: { enhanceSuccessBonus: 0.01, description: '+1% success (7% total)' },
      40: { enhanceSuccessBonus: 0.01, description: '+1% success (8% total)' },
      45: { enhanceSuccessBonus: 0.01, essenceCostReduction: 0.05, description: '+1% success, +5% essence reduction (15% total)' },
      50: { enhanceSuccessBonus: 0.02, title: 'Master Enchanter', description: '+2% success (10% total), Master title' },
    },
  },
};

// ============================================================================
// BONUS CALCULATION HELPERS
// ============================================================================

/**
 * Get total yield bonus for a specific resource from all relevant skills
 * @param {string} resourceId - Resource being gathered
 * @param {Object} lifeSkills - Player's life skills state { skillId: { level, xp } }
 * @returns {number} Total yield multiplier (e.g., 0.30 for +30%)
 */
export function getYieldBonus(resourceId, lifeSkills) {
  let totalBonus = 0;

  for (const [skillId, skillData] of Object.entries(LIFE_SKILLS)) {
    if (skillData.category !== 'gathering') continue;
    if (!skillData.gatherResources.includes(resourceId)) continue;

    const playerSkill = lifeSkills?.[skillId];
    if (!playerSkill) continue;

    // Sum up all milestone bonuses up to current level
    for (const [level, milestone] of Object.entries(skillData.milestones)) {
      if (playerSkill.level >= parseInt(level)) {
        totalBonus += milestone.yieldBonus || 0;
      }
    }
  }

  return totalBonus;
}

/**
 * Get total rare drop chance bonus for a biome
 * @param {string} biomeId - Current biome
 * @param {Object} lifeSkills - Player's life skills state
 * @returns {number} Rare drop chance (e.g., 0.05 for 5%)
 */
export function getRareDropChance(biomeId, lifeSkills) {
  let totalChance = 0;

  for (const [skillId, skillData] of Object.entries(LIFE_SKILLS)) {
    if (skillData.category !== 'gathering') continue;
    if (!skillData.biomes?.includes(biomeId)) continue;

    const playerSkill = lifeSkills?.[skillId];
    if (!playerSkill) continue;

    for (const [level, milestone] of Object.entries(skillData.milestones)) {
      if (playerSkill.level >= parseInt(level)) {
        totalChance += milestone.rareDropChance || 0;
      }
    }
  }

  return totalChance;
}

/**
 * Get auto-gather speed bonus from skills
 * @param {string} biomeId - Current biome
 * @param {Object} lifeSkills - Player's life skills state
 * @returns {number} Speed multiplier bonus (e.g., 0.25 for +25%)
 */
export function getAutoGatherBonus(biomeId, lifeSkills) {
  let totalBonus = 0;

  for (const [skillId, skillData] of Object.entries(LIFE_SKILLS)) {
    if (skillData.category !== 'gathering') continue;
    if (!skillData.biomes?.includes(biomeId)) continue;

    const playerSkill = lifeSkills?.[skillId];
    if (!playerSkill) continue;

    for (const [level, milestone] of Object.entries(skillData.milestones)) {
      if (playerSkill.level >= parseInt(level)) {
        totalBonus += milestone.autoGatherBonus || 0;
      }
    }
  }

  return totalBonus;
}

/**
 * Get forge success bonus from smithing skill
 * @param {Object} lifeSkills - Player's life skills state
 * @returns {number} Success rate bonus (e.g., 0.10 for +10%)
 */
export function getForgeSuccessBonus(lifeSkills) {
  const smithing = lifeSkills?.smithing;
  if (!smithing) return 0;

  let totalBonus = 0;
  const skillData = LIFE_SKILLS.smithing;

  for (const [level, milestone] of Object.entries(skillData.milestones)) {
    if (smithing.level >= parseInt(level)) {
      totalBonus += milestone.forgeSuccessBonus || 0;
    }
  }

  return totalBonus;
}

/**
 * Get gem cost reduction from smithing skill
 * @param {Object} lifeSkills - Player's life skills state
 * @returns {number} Cost reduction (e.g., 0.10 for 10% off)
 */
export function getGemCostReduction(lifeSkills) {
  const smithing = lifeSkills?.smithing;
  if (!smithing) return 0;

  let totalReduction = 0;
  const skillData = LIFE_SKILLS.smithing;

  for (const [level, milestone] of Object.entries(skillData.milestones)) {
    if (smithing.level >= parseInt(level)) {
      totalReduction += milestone.gemCostReduction || 0;
    }
  }

  return totalReduction;
}

/**
 * Get enhance success bonus from enchanting skill
 * @param {Object} lifeSkills - Player's life skills state
 * @returns {number} Success rate bonus (e.g., 0.05 for +5%)
 */
export function getEnhanceSuccessBonus(lifeSkills) {
  const enchanting = lifeSkills?.enchanting;
  if (!enchanting) return 0;

  let totalBonus = 0;
  const skillData = LIFE_SKILLS.enchanting;

  for (const [level, milestone] of Object.entries(skillData.milestones)) {
    if (enchanting.level >= parseInt(level)) {
      totalBonus += milestone.enhanceSuccessBonus || 0;
    }
  }

  return totalBonus;
}

/**
 * Get star essence cost reduction from enchanting skill
 * @param {Object} lifeSkills - Player's life skills state
 * @returns {number} Cost reduction (e.g., 0.15 for 15% off)
 */
export function getEssenceCostReduction(lifeSkills) {
  const enchanting = lifeSkills?.enchanting;
  if (!enchanting) return 0;

  let totalReduction = 0;
  const skillData = LIFE_SKILLS.enchanting;

  for (const [level, milestone] of Object.entries(skillData.milestones)) {
    if (enchanting.level >= parseInt(level)) {
      totalReduction += milestone.essenceCostReduction || 0;
    }
  }

  return totalReduction;
}

/**
 * Determine which skill gains XP from gathering a specific resource
 * @param {string} resourceId - Resource being gathered
 * @returns {string|null} Skill ID that gains XP, or null
 */
export function getSkillForResource(resourceId) {
  for (const [skillId, skillData] of Object.entries(LIFE_SKILLS)) {
    if (skillData.category === 'gathering' && skillData.gatherResources.includes(resourceId)) {
      return skillId;
    }
  }
  return null;
}

/**
 * Determine which skill gains XP from a biome
 * @param {string} biomeId - Current biome
 * @returns {string[]} Array of skill IDs that can gain XP
 */
export function getSkillsForBiome(biomeId) {
  const skills = [];
  for (const [skillId, skillData] of Object.entries(LIFE_SKILLS)) {
    if (skillData.category === 'gathering' && skillData.biomes?.includes(biomeId)) {
      skills.push(skillId);
    }
  }
  return skills;
}

/**
 * Calculate XP gained from a gather action
 * @param {string} resourceId - Resource gathered
 * @param {number} amount - Amount gathered
 * @param {number} tileQuality - Tile quality multiplier (0.7-1.3)
 * @returns {{ skillId: string, xp: number }|null}
 */
export function calculateGatherXp(resourceId, amount, tileQuality = 1.0) {
  const skillId = getSkillForResource(resourceId);
  if (!skillId) return null;

  let xp = LIFE_SKILLS_CONFIG.baseXpPerGather * amount;

  // Apply tile quality bonus if enabled
  if (LIFE_SKILLS_CONFIG.tileQualityXpBonus) {
    xp *= tileQuality;
  }

  return {
    skillId,
    xp: Math.floor(xp),
  };
}

/**
 * Get all unlocked milestones for a skill
 * @param {string} skillId - Skill ID
 * @param {number} level - Current level
 * @returns {Array<{ level: number, ...milestone }>}
 */
export function getUnlockedMilestones(skillId, level) {
  const skillData = LIFE_SKILLS[skillId];
  if (!skillData) return [];

  const unlocked = [];
  for (const [milestoneLevel, milestone] of Object.entries(skillData.milestones)) {
    if (level >= parseInt(milestoneLevel)) {
      unlocked.push({ level: parseInt(milestoneLevel), ...milestone });
    }
  }

  return unlocked.sort((a, b) => a.level - b.level);
}

/**
 * Get next milestone for a skill
 * @param {string} skillId - Skill ID
 * @param {number} level - Current level
 * @returns {{ level: number, ...milestone }|null}
 */
export function getNextMilestone(skillId, level) {
  const skillData = LIFE_SKILLS[skillId];
  if (!skillData) return null;

  const milestoneLevels = Object.keys(skillData.milestones)
    .map(Number)
    .sort((a, b) => a - b);

  for (const milestoneLevel of milestoneLevels) {
    if (milestoneLevel > level) {
      return { level: milestoneLevel, ...skillData.milestones[milestoneLevel] };
    }
  }

  return null; // Maxed out
}

/**
 * Initialize default life skills state for a new player
 * @returns {Object} Initial life skills state
 */
export function getInitialLifeSkills() {
  const skills = {};
  for (const skillId of Object.keys(LIFE_SKILLS)) {
    skills[skillId] = {
      level: 1,
      xp: 0,
    };
  }
  return skills;
}

/**
 * Get skill display info for UI
 * @param {string} skillId - Skill ID
 * @param {Object} playerSkill - Player's skill state { level, xp }
 * @returns {Object} Display info
 */
export function getSkillDisplayInfo(skillId, playerSkill) {
  const skillData = LIFE_SKILLS[skillId];
  if (!skillData || !playerSkill) return null;

  const { level, xp } = playerSkill;
  const xpForNext = getXpToNextLevel(level);
  const isMaxed = level >= LIFE_SKILLS_CONFIG.maxLevel;
  const progress = isMaxed ? 1 : xp / xpForNext;
  const nextMilestone = getNextMilestone(skillId, level);
  const unlockedMilestones = getUnlockedMilestones(skillId, level);

  // Calculate total bonuses
  let totalYieldBonus = 0;
  let totalRareDropChance = 0;
  let totalAutoGatherBonus = 0;
  let forgeSuccessBonus = 0;
  let enhanceSuccessBonus = 0;

  for (const milestone of unlockedMilestones) {
    totalYieldBonus += milestone.yieldBonus || 0;
    totalRareDropChance += milestone.rareDropChance || 0;
    totalAutoGatherBonus += milestone.autoGatherBonus || 0;
    forgeSuccessBonus += milestone.forgeSuccessBonus || 0;
    enhanceSuccessBonus += milestone.enhanceSuccessBonus || 0;
  }

  return {
    ...skillData,
    level,
    xp,
    xpForNext,
    isMaxed,
    progress,
    progressPercent: Math.round(progress * 100),
    nextMilestone,
    unlockedMilestones,
    totalBonuses: {
      yieldBonus: totalYieldBonus,
      rareDropChance: totalRareDropChance,
      autoGatherBonus: totalAutoGatherBonus,
      forgeSuccessBonus,
      enhanceSuccessBonus,
    },
  };
}

// ============================================================================
// SKILLING ACTIONS (MWI-Style XP Grind)
// ============================================================================

export const SKILLING_ACTIONS = {
  // === LUMBERING ===
  chop_oak: {
    id: 'chop_oak',
    skill: 'lumbering',
    name: 'Chop Oak',
    icon: '🪓',
    levelReq: 1,
    actionTime: 3,
    xpGain: 15,
  },
  chop_maple: {
    id: 'chop_maple',
    skill: 'lumbering',
    name: 'Chop Maple',
    icon: '🍁',
    levelReq: 10,
    actionTime: 4,
    xpGain: 35,
  },
  chop_elder: {
    id: 'chop_elder',
    skill: 'lumbering',
    name: 'Chop Elder',
    icon: '🌳',
    levelReq: 25,
    actionTime: 5,
    xpGain: 75,
  },
  chop_ancient: {
    id: 'chop_ancient',
    skill: 'lumbering',
    name: 'Chop Ancient',
    icon: '🌲',
    levelReq: 40,
    actionTime: 6,
    xpGain: 150,
  },

  // === MINING ===
  mine_stone: {
    id: 'mine_stone',
    skill: 'mining',
    name: 'Mine Stone',
    icon: '🗻',
    levelReq: 1,
    actionTime: 3,
    xpGain: 15,
  },
  mine_iron: {
    id: 'mine_iron',
    skill: 'mining',
    name: 'Mine Iron',
    icon: '⛏️',
    levelReq: 10,
    actionTime: 4,
    xpGain: 35,
  },
  mine_coal: {
    id: 'mine_coal',
    skill: 'mining',
    name: 'Mine Coal',
    icon: '⚫',
    levelReq: 20,
    actionTime: 5,
    xpGain: 60,
  },
  mine_mithril: {
    id: 'mine_mithril',
    skill: 'mining',
    name: 'Mine Mithril',
    icon: '💎',
    levelReq: 35,
    actionTime: 6,
    xpGain: 120,
  },

  // === FORAGING ===
  gather_berries: {
    id: 'gather_berries',
    skill: 'foraging',
    name: 'Gather Berries',
    icon: '🍇',
    levelReq: 1,
    actionTime: 2,
    xpGain: 10,
  },
  gather_herbs: {
    id: 'gather_herbs',
    skill: 'foraging',
    name: 'Gather Herbs',
    icon: '🌿',
    levelReq: 10,
    actionTime: 3,
    xpGain: 25,
  },
  gather_mushrooms: {
    id: 'gather_mushrooms',
    skill: 'foraging',
    name: 'Gather Mushrooms',
    icon: '🍄',
    levelReq: 20,
    actionTime: 4,
    xpGain: 50,
  },
  gather_rare: {
    id: 'gather_rare',
    skill: 'foraging',
    name: 'Gather Rare Herbs',
    icon: '🌸',
    levelReq: 35,
    actionTime: 5,
    xpGain: 100,
  },

  // === PROSPECTING ===
  pan_gold: {
    id: 'pan_gold',
    skill: 'prospecting',
    name: 'Pan for Gold',
    icon: '🔍',
    levelReq: 1,
    actionTime: 4,
    xpGain: 20,
  },
  prospect_ore: {
    id: 'prospect_ore',
    skill: 'prospecting',
    name: 'Prospect Ore',
    icon: '💰',
    levelReq: 15,
    actionTime: 5,
    xpGain: 45,
  },
  dig_treasure: {
    id: 'dig_treasure',
    skill: 'prospecting',
    name: 'Dig Treasure',
    icon: '🏆',
    levelReq: 30,
    actionTime: 6,
    xpGain: 90,
  },
  excavate_riches: {
    id: 'excavate_riches',
    skill: 'prospecting',
    name: 'Excavate Riches',
    icon: '👑',
    levelReq: 45,
    actionTime: 8,
    xpGain: 180,
  },

  // === ARCHAEOLOGY ===
  study_ruins: {
    id: 'study_ruins',
    skill: 'archaeology',
    name: 'Study Ruins',
    icon: '🏛️',
    levelReq: 1,
    actionTime: 5,
    xpGain: 25,
  },
  decipher_text: {
    id: 'decipher_text',
    skill: 'archaeology',
    name: 'Decipher Texts',
    icon: '📜',
    levelReq: 15,
    actionTime: 6,
    xpGain: 55,
  },
  excavate_site: {
    id: 'excavate_site',
    skill: 'archaeology',
    name: 'Excavate Site',
    icon: '🗿',
    levelReq: 30,
    actionTime: 8,
    xpGain: 110,
  },
  uncover_secrets: {
    id: 'uncover_secrets',
    skill: 'archaeology',
    name: 'Uncover Secrets',
    icon: '✨',
    levelReq: 45,
    actionTime: 10,
    xpGain: 220,
  },
};

export const SKILLING_CONFIG = {
  maxQueueSize: 10,
  defaultRepeatCount: 10,
};

/**
 * Get all skilling actions for a specific skill
 * @param {string} skillId - Skill ID
 * @returns {Object[]} Array of actions for that skill
 */
export function getActionsForSkill(skillId) {
  return Object.values(SKILLING_ACTIONS).filter(a => a.skill === skillId);
}

/**
 * Get unlocked actions for a skill based on level
 * @param {string} skillId - Skill ID
 * @param {number} level - Current skill level
 * @returns {Object[]} Array of unlocked actions
 */
export function getUnlockedActions(skillId, level) {
  return getActionsForSkill(skillId).filter(a => a.levelReq <= level);
}

export default LIFE_SKILLS;
